import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LeadModal } from "../LeadModal";

export default function Footer() {
  const zoneRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const x = useSpring(useMotionValue(0), { stiffness: 55, damping: 18, mass: 0.9 });
  const y = useSpring(useMotionValue(0), { stiffness: 55, damping: 18, mass: 0.9 });

  const onMouseMove = (e) => {
    const r = zoneRef.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    x.set(dx * 0.22);
    y.set(dy * 0.26);
  };
  const onMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <footer data-testid="site-footer" className="relative px-5 pb-10 pt-24 md:px-10 md:pt-36">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col items-center text-center"
      >
        <span className="text-[11px] font-medium tracking-[0.45em] text-white/45">
          ¿LISTO PARA CONSTRUIR?
        </span>

        {/* Magnetic zone */}
        <div
          ref={zoneRef}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          className="mt-10 flex items-center justify-center px-6 py-10 md:px-24 md:py-16"
        >
          <motion.button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="build-empire-cta"
            style={{ x, y }}
            whileTap={{ scale: 0.965 }}
            className="inline-flex items-center gap-4 rounded-full border border-white/15 px-10 py-6 font-display text-2xl font-semibold tracking-tight text-white transition-colors duration-700 hover:border-white hover:bg-white hover:text-black md:gap-6 md:px-16 md:py-8 md:text-4xl"
          >
            BUILD YOUR EMPIRE
            <ArrowUpRight className="h-6 w-6 md:h-9 md:w-9" aria-hidden="true" />
          </motion.button>
        </div>
      </motion.div>

      <div className="mx-auto mt-20 flex max-w-7xl flex-col items-center justify-between gap-5 border-t border-white/10 pt-8 md:mt-28 md:flex-row md:gap-3">
        <p data-testid="footer-copyright" className="text-xs text-white/40">
          © 2026 Servin Labs. All rights reserved.
        </p>
        <div className="flex items-center gap-6" data-testid="footer-socials">
          <a
            href="https://www.tiktok.com/@servin.labs.oficial"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Servin Labs en TikTok"
            data-testid="footer-tiktok-link"
            className="text-white/45 transition-all duration-300 hover:text-white hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.55)]"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]" aria-hidden="true">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/servinlabs.oficial/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Servin Labs en Instagram"
            data-testid="footer-instagram-link"
            className="text-white/45 transition-all duration-300 hover:text-white hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.55)]"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]" aria-hidden="true">
              <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
            </svg>
          </a>
        </div>
        <a
          href="mailto:angelobusinessai@gmail.com"
          data-testid="footer-email-link"
          className="text-xs text-white/40 transition-colors duration-300 hover:text-white"
        >
          angelobusinessai@gmail.com
        </a>
      </div>
      <LeadModal open={modalOpen} onOpenChange={setModalOpen} />
    </footer>
  );
}
